/**
 * Integrytas Group - Common Security Module (CSM)
 * Contextual AI Assistance — Smart Query bar for the standard data grid.
 *
 * Users type a natural-language request ("active admins added this year, oldest
 * first"); the csm-ai gateway returns a native AiQuery (filters + sorts) which
 * is applied to the already-loaded grid rows in memory via applyAiQuery(). No
 * SQL is generated and nothing reaches the database — the AI only reshapes what
 * the user can already see.
 *
 * Renders nothing when AI is not enabled for the screen (deny-by-default).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, X } from 'lucide-react';
import { aiClient } from '../../lib/ai/ai_client';
import { describeFilter } from '../../lib/ai/grid_filter';
import type { AiColumnSchema, AiQuery } from '../../lib/ai/types';

export interface SmartQueryBarProps {
  pageCode:     string;
  columns:      AiColumnSchema[];
  permissions?: Record<string, unknown>;
  /** The currently applied AI query (controlled by the parent), or null. */
  query:        AiQuery | null;
  onApply:      (query: AiQuery | null) => void;
  /** From the screen config (config.allow_nlq). When false, renders nothing. */
  enabled?:     boolean;
}

export function SmartQueryBar({
  pageCode, columns, permissions, query, onApply, enabled = true,
}: SmartQueryBarProps) {
  const { t } = useTranslation();
  const [text, setText]   = useState('');
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!enabled) return null;

  const run = async () => {
    const q = text.trim();
    if (!q) return;
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await aiClient.nlq({
        pageCode, query: q, columns, permissions,
      });
      if (e || !data) { setError(e?.message ?? 'CSM.AI.NO_RESULT'); return; }
      if (data.filters.length === 0 && data.sorts.length === 0) {
        setError(t('ai.smart_query.no_match'));
        return;
      }
      onApply({ filters: data.filters, sorts: data.sorts, explanation: data.explanation });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const clear = () => { onApply(null); setText(''); setError(null); };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <div className="relative flex items-center">
          <Sparkles size={13} className="pointer-events-none absolute left-2 text-primary" />
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void run(); }}
            placeholder={t('ai.smart_query.placeholder')}
            className="h-7 w-96 rounded border border-csm-border pl-7 pr-2 text-csm-md focus:border-primary focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => void run()}
          disabled={busy || !text.trim()}
          className="h-7 rounded border border-primary bg-primary px-2 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60"
        >
          {busy ? t('ai.smart_query.thinking') : t('ai.smart_query.run')}
        </button>
        {query && (
          <button
            type="button"
            onClick={clear}
            className="flex h-7 items-center gap-1 rounded border border-csm-border bg-white px-2 text-csm-sm text-csm-muted hover:text-primary"
          >
            <X size={12} /> {t('ai.smart_query.clear')}
          </button>
        )}
        {error && <span className="text-csm-xs text-csm-danger">{error}</span>}
      </div>

      {query && (query.filters.length > 0 || query.sorts.length > 0) && (
        <div className="flex flex-wrap items-center gap-1 text-csm-xs">
          {query.explanation && (
            <span className="mr-1 italic text-csm-muted">{query.explanation}</span>
          )}
          {query.filters.map((f, i) => (
            <span key={`f${i}`} className="rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-primary">
              {describeFilter(f)}
            </span>
          ))}
          {query.sorts.map((s, i) => (
            <span key={`s${i}`} className="rounded border border-csm-border bg-csm-surface-alt px-1.5 py-0.5 text-csm-ink-soft">
              ↕ {s.column} {s.dir}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
